import React, { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Search, StickyNote, Trash2, Edit2, X, Save, FileText, Calendar, BookOpen } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Textarea } from './ui/textarea';
import { useNotesStore } from '../../stores/notesStore';
import { useLibraryStore } from '../../stores/libraryStore';
import { toast } from 'sonner';
import { useI18n, useLocaleFormat } from '../../i18n';

export function NotesView() {
  const { t } = useI18n();
  const fmt = useLocaleFormat();
  const { getToken } = useAuth();
  const { 
    notes, 
    currentDocumentNotes, 
    fetchAllNotes, 
    fetchDocumentNotes,
    deleteNote, 
    updateNote,
    isLoading 
  } = useNotesStore();
  const { selectedMaterial, selectMaterial, materials } = useLibraryStore();

  const [searchQuery, setSearchQuery] = useState('');
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [viewMode, setViewMode] = useState<'all' | 'current'>('all');

  // Fetch notes on mount
  useEffect(() => {
    const loadNotes = async () => {
      const token = await getToken();
      if (token) {
        fetchAllNotes(token);
        if (selectedMaterial) {
          fetchDocumentNotes(selectedMaterial.id, token);
        }
      }
    };
    loadNotes();
  }, [getToken, fetchAllNotes, fetchDocumentNotes, selectedMaterial]);

  // Filter notes
  const displayNotes = viewMode === 'current' && selectedMaterial 
    ? (currentDocumentNotes || [])
    : (notes || []);
  
  const filteredNotes = displayNotes.filter(note =>
    note.note_content?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.selected_text?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.document_title?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group notes by document
  const groupedNotes = filteredNotes.reduce((acc, note) => {
    const docId = note.document_id || 'uncategorized';
    if (!acc[docId]) {
      acc[docId] = {
        title: note.document_title || t('notes.uncategorized'),
        notes: []
      };
    }
    acc[docId].notes.push(note);
    return acc;
  }, {} as Record<string, { title: string; notes: typeof filteredNotes }>);

  // Handle note delete
  const handleDelete = async (noteId: string) => {
    const token = await getToken();
    if (!token) return;

    if (confirm(t('notes.confirmDelete'))) {
      try {
        await deleteNote(noteId, token);
        toast.success(t('notes.toast.deleted'));
      } catch (error) {
        toast.error(t('notes.toast.deleteFailed'));
      }
    }
  };

  // Handle note edit
  const handleStartEdit = (noteId: string, content: string) => {
    setEditingNote(noteId);
    setEditContent(content);
  };

  const handleSaveEdit = async (noteId: string) => {
    const token = await getToken();
    if (!token) return;

    try {
      await updateNote(noteId, { note_content: editContent }, token);
      setEditingNote(null);
      setEditContent('');
      toast.success(t('notes.toast.updated'));
    } catch (error) {
      toast.error(t('notes.toast.updateFailed'));
    }
  };

  // Navigate to document
  const handleNavigateToDocument = (documentId: string) => {
    const material = materials.find(m => m.id === documentId);
    if (material) {
      selectMaterial(material);
      toast.info(t('library.toast.opened', { name: material.original_filename || material.filename }));
    }
  };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden bg-gradient-to-br from-white to-gray-50">
      {/* Header */}
      <div className="shrink-0 border-b border-white/20 bg-white/60 backdrop-blur-xl px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h1 className="text-2xl font-display text-[#434343]">{t('notes.title')}</h1>
            <div className="flex items-center gap-2">
            <Button
              variant={viewMode === 'all' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('all')}
              className={viewMode === 'all' ? 'bg-[#7367FF] hover:bg-[#5f56e6] text-white' : ''}
            >
              {t('notes.allNotes')}
            </Button>
            {selectedMaterial && (
              <Button
                variant={viewMode === 'current' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('current')}
                className={viewMode === 'current' ? 'bg-[#7367FF] hover:bg-[#5f56e6] text-white' : ''}
                >
                {t('notes.currentDocument')}
              </Button>
            )}
          </div>
        </div>
        
        {/* Search */}
        <div className="relative">
          <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#434343]/60" />
          <Input
            aria-label={t('notes.searchPlaceholder')}
            placeholder={t('notes.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="ps-10 bg-white/60 border-white/40 focus:border-[#7367FF]"
          />
        </div>
      </div>

      {/* Notes List */}
      <ScrollArea className="min-h-0 flex-1" viewportProps={{ tabIndex: 0, role: 'region', 'aria-label': t('notes.title') }}>
        <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin h-8 w-8 border-4 border-[#7367FF] border-t-transparent rounded-full"></div>
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-20 h-20 rounded-full bg-[#7367FF]/10 flex items-center justify-center mb-4">
              <StickyNote className="h-10 w-10 text-[#7367FF]" />
            </div>
            <h3 className="text-lg font-semibold text-[#434343] mb-2">
              {searchQuery ? t('notes.emptyFoundTitle') : t('notes.emptyTitle')}
            </h3>
            <p className="text-sm text-[#434343]/60 max-w-sm">
              {searchQuery ? t('notes.emptySearchBody') : t('notes.emptyBody')}
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {Object.entries(groupedNotes).map(([docId, group]) => (
              <div key={docId}>
                {/* Document Header */}
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="h-4 w-4 text-[#7367FF]" />
                  <h2 dir="auto" className="text-sm font-medium text-[#434343]">{group.title}</h2>
                  {docId !== 'uncategorized' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleNavigateToDocument(docId)}
                      className="text-xs text-[#7367FF] hover:text-[#5f56e6]"
                    >
                      {t('notes.openDocument')}
                    </Button>
                  )}
                </div>

                {/* Notes Grid */}
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {group.notes.map((note) => (
                    <div
                      key={note.id}
                      className="bg-white/60 backdrop-blur-xl border border-white/40 rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
                    >
                      {editingNote === note.id ? (
                        <>
                          <Textarea
                            dir="auto"
                            aria-label={t("notes.editContent")}
                            value={editContent}
                            onChange={(e) => setEditContent(e.target.value)}
                            className="mb-3 bg-white/60 border-white/40 focus:border-[#7367FF]"
                            rows={4}
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => handleSaveEdit(note.id)}
                              className="bg-[#7367FF] hover:bg-[#5f56e6] text-white"
                            >
                              <Save className="h-3 w-3 me-1" />
                              {t('common.save')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingNote(null)}
                            >
                              <X className="h-3 w-3 me-1" />
                              {t('common.cancel')}
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Note Type Badge */}
                          <div className="flex items-center justify-between mb-2">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                              note.note_type === 'explanation' 
                                ? 'bg-[#7367FF]/10 text-[#7367FF]'
                                : 'bg-[#3CFF9B]/10 text-[#3CFF9B]'
                            }`}>
                              {note.note_type === 'explanation' ? t('notes.typeExplanation') : t('notes.typeHighlight')}
                            </span>
                            {note.page_number && (
                              <span className="text-xs text-[#434343]/60">
                                {t('notes.pageLabel', { page: note.page_number })}
                              </span>
                            )}
                          </div>

                          {/* Selected Text */}
                          {note.selected_text && note.selected_text !== note.note_content && (
                            <div className="mb-2 p-2 bg-[#7367FF]/5 rounded-lg border-s-2 border-[#7367FF]">
                              <p dir="auto" className="text-xs text-[#434343]/80 italic line-clamp-2">
                                "{note.selected_text}"
                              </p>
                            </div>
                          )}

                          {/* Note Content */}
                          <p dir="auto" className="text-sm text-[#434343] mb-3 line-clamp-4">
                            {note.note_content}
                          </p>

                          {/* Footer */}
                          <div className="flex items-center justify-between pt-2 border-t border-white/20">
                            <span className="text-xs text-[#434343]/60 flex items-center gap-1">
                              <Calendar className="h-3 w-3" />
                              {fmt.date(note.created_at)}
                            </span>
                            <div className="flex gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={t("common.edit")}
                                onClick={() => handleStartEdit(note.id, note.note_content)}
                                className="h-7 w-7 p-0"
                              >
                                <Edit2 className="h-3 w-3 text-[#434343]" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                aria-label={t("common.delete")}
                                onClick={() => handleDelete(note.id)}
                                className="h-7 w-7 p-0 hover:bg-red-50"
                              >
                                <Trash2 className="h-3 w-3 text-red-500" />
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
        </div>
      </div>
            ))}
          </div>
        )}
        </div>
      </ScrollArea>
    </div>
  );
}
